import dayjs from 'dayjs';
import _ from 'lodash';

import { jsonStringify } from 'utils/json';

import {
  DISCLOSURE_BUNDLE_NAME_PATTERN,
  EMPTY_VALUE,
  KEY_NUMBER_CONFIG,
  LOAN_ESTIMATE_FORM_NAME_PATTERN,
  PERCENT_DECIMAL_SCALE,
  PREVIEW_PROGRESS_CEILING,
  PREVIEW_PROGRESS_COMPLETE,
  PREVIEW_PROGRESS_DURATION_MS,
  PreviewStatus,
} from './previewGeneration.constants';

const toFiniteNumber = value => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const getBundleName = bundle => bundle?.name ?? bundle?.formBundleName ?? '';

const resolveDisclosureBundle = (bundles, { preferredBundleId } = {}) => {
  const list = Array.isArray(bundles) ? bundles.filter(Boolean) : [];

  if (!list.length) {
    return null;
  }

  if (preferredBundleId) {
    const matchById = list.find(
      bundle => (bundle?.formBundleId ?? bundle?.id) === preferredBundleId,
    );

    if (matchById) {
      return matchById;
    }
  }

  const matchByName = list.find(bundle =>
    DISCLOSURE_BUNDLE_NAME_PATTERN.test(getBundleName(bundle)),
  );

  return matchByName ?? list[0];
};

const getFormId = form => form?.formElementId ?? form?.formId ?? form?.id ?? null;

const getFormName = form => form?.name ?? form?.formName ?? '';

const getBundleFormList = bundle => {
  if (Array.isArray(bundle?.forms)) {
    return bundle.forms;
  }

  if (Array.isArray(bundle?.elements)) {
    return bundle.elements;
  }

  return [];
};

const resolveLoanEstimateForm = bundle => {
  const forms = getBundleFormList(bundle);
  const usable = forms.map(form => ({ formId: getFormId(form), name: getFormName(form) }));
  const withId = usable.filter(form => !!form.formId);

  if (!withId.length) {
    return null;
  }

  const matchByName = withId.find(form => LOAN_ESTIMATE_FORM_NAME_PATTERN.test(form.name));

  return matchByName ?? withId[0];
};

export const resolvePreviewTarget = (bundles, options = {}) => {
  const bundle = resolveDisclosureBundle(bundles, options);
  const form = resolveLoanEstimateForm(bundle);

  if (!bundle || !form) {
    return null;
  }

  return {
    formBundleId: bundle.formBundleId ?? bundle.id ?? null,
    formBundleName: getBundleName(bundle),
    formId: form.formId,
    formName: form.name,
  };
};

export const computePreviewProgress = ({ status, elapsedMs = 0 }) => {
  if (status === PreviewStatus.READY) {
    return PREVIEW_PROGRESS_COMPLETE;
  }

  if (status !== PreviewStatus.GENERATING) {
    return 0;
  }

  const ratio = Math.min(Math.max(elapsedMs, 0) / PREVIEW_PROGRESS_DURATION_MS, 1);
  const eased = 1 - (1 - ratio) ** 2;

  return Math.min(Math.round(eased * PREVIEW_PROGRESS_CEILING), PREVIEW_PROGRESS_CEILING);
};

const stableStringify = value => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (_.isObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map(key => `${key}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }

  return jsonStringify(value ?? null);
};

// The stale-preview key covers ONLY the values that change the generated Loan
// Estimate PDF (loan amount, rate, APR, cash-to-close). Signer email/second
// factor come from the persisted borrower record and delivery method is a
// wizard-local workflow declaration — none alter the LE, so none invalidate the
// broker's review (per the "attested LE version equals sent version" AC).
export const computePreviewVersionHash = ({ loanAmount, rate, apr, cashToClose } = {}) =>
  stableStringify({
    loanAmount: toFiniteNumber(loanAmount),
    rate: toFiniteNumber(rate),
    apr: toFiniteNumber(apr),
    cashToClose: toFiniteNumber(cashToClose),
  });

export const isPreviewStale = (reviewedVersionHash, currentVersionHash) =>
  !reviewedVersionHash || reviewedVersionHash !== currentVersionHash;

const formatPercentValue = value => {
  const parsed = toFiniteNumber(value);

  if (parsed === null) {
    return null;
  }

  return `${Number(parsed.toFixed(PERCENT_DECIMAL_SCALE))}%`;
};

export const formatEarliestClosing = value => {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format('MMM D') : null;
};

const formatKeyNumberValue = (config, rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return EMPTY_VALUE;
  }

  if (config.type) {
    return { value: rawValue, type: config.type };
  }

  if (config.isPercent) {
    return formatPercentValue(rawValue) ?? EMPTY_VALUE;
  }

  return rawValue;
};

export const buildKeyNumbers = (source = {}) =>
  KEY_NUMBER_CONFIG.map(config => {
    const formatted = formatKeyNumberValue(config, source[config.id]);
    const isTyped = _.isObject(formatted);

    return {
      id: config.id,
      title: config.title,
      highlight: !!config.highlight,
      value: isTyped ? formatted.value : formatted,
      type: isTyped ? formatted.type : undefined,
    };
  });
